/**
 * Two-way synchronization between the local library (IndexedDB) and the
 * library backend.
 *
 * - pull: drawings with a newer server version replace the local copy; when
 *   the local copy has unpushed changes too, see `handleDivergence()`
 * - push: dirty drawings are uploaded with the server version they are based
 *   on; on 409 the server copy is handled the same way
 * - drawings in conflict are not synced until the user resolves them
 *
 * Works offline: everything is stored locally first and pushed when the
 * server is reachable again.
 *
 * The backend announces every write on a Server-Sent Events stream, so
 * changes made elsewhere (other devices, the MCP server) are pulled right
 * away; polling remains as a fallback, less often while the stream is open.
 *
 * Every user has a library of their own on the server; the local one belongs
 * to the user who last synced it (see `checkLibraryUser()`).
 */

import { LIBRARY_API_URL, LibraryAPI, LibraryAPIError } from "./api";
import { handleDivergence, storeServerCopy } from "./remote";
import {
  LIBRARY_USER_KEY,
  clearDeviceNameDirty,
  getDeviceId,
  getDeviceName,
  getLibraryUser,
  isDeviceNameDirty,
  setLibraryUser,
} from "./settings";
import { DrawingStore, emitLibraryChange, onLibraryChange } from "./store";

import type { DrawingId, DrawingMeta, Folder } from "./types";

const SYNC_INTERVAL_MS = 30 * 1000;
/** polling interval while the change notifications stream is connected */
const SYNC_INTERVAL_CONNECTED_MS = 5 * 60 * 1000;
const SYNC_AFTER_CHANGE_DELAY_MS = 4 * 1000;
/** coalesces notifications arriving close together into a single sync */
const SYNC_AFTER_EVENT_DELAY_MS = 300;
/** retry delays when the stream can't be opened (EventSource gave up, e.g.
 * backend restarting): doubled at each failure */
const EVENTS_RETRY_MIN_MS = 5 * 1000;
const EVENTS_RETRY_MAX_MS = 60 * 1000;
const LOCK_NAME = "excalidraw-drawings-sync";

/** signedOut: the server answered 401, e.g. the login session in front of
 * the app (oauth2-proxy) expired; reloading the page logs in again */
export type SyncState = "idle" | "syncing" | "offline" | "error" | "signedOut";

export type SyncStatus = {
  state: SyncState;
  lastSyncAt: number | null;
  pending: number;
  conflicts: number;
  error: string | null;
};

let status: SyncStatus = {
  state: navigator.onLine ? "idle" : "offline",
  lastSyncAt: null,
  pending: 0,
  conflicts: 0,
  error: null,
};

const statusListeners = new Set<(status: SyncStatus) => void>();

const setStatus = (next: Partial<SyncStatus>) => {
  status = { ...status, ...next };
  statusListeners.forEach((listener) => listener(status));
};

const refreshPendingCount = async () => {
  const metas = await DrawingStore.listMeta();
  setStatus({
    pending: metas.filter((meta) => meta.dirty && !meta.conflict).length,
    conflicts: metas.filter((meta) => meta.conflict && !meta.purged).length,
  });
};

const withLock = <T>(fn: () => Promise<T>): Promise<T> =>
  navigator.locks?.request
    ? (navigator.locks.request(LOCK_NAME, fn) as Promise<T>)
    : fn();

/** push a dirty drawing; returns false when it must be pushed again (the
 * server copy was newer and has been combined with ours) */
const pushDrawing = async (meta: DrawingMeta): Promise<boolean> => {
  if (meta.purged) {
    await LibraryAPI.purge(meta.id, getDeviceId());
    await DrawingStore.remove(meta.id);
    return true;
  }
  const scene = await DrawingStore.getScene(meta.id);
  if (!scene) {
    return true;
  }
  const missingFileIds = await LibraryAPI.missingFiles(meta.fileIds);
  for (const file of await DrawingStore.getFiles(missingFileIds)) {
    await LibraryAPI.putFile(file);
  }
  const result = await LibraryAPI.put(
    meta.id,
    meta.serverVersion,
    {
      name: meta.name,
      createdAt: meta.createdAt,
      updatedAt: meta.updatedAt,
      metaUpdatedAt: meta.metaUpdatedAt ?? meta.updatedAt,
      deleted: meta.deleted,
      folderId: meta.folderId,
      thumbnail: meta.thumbnail,
      fileIds: meta.fileIds,
      scene,
    },
    {
      device: getDeviceName(),
      deviceId: getDeviceId(),
      snapshot: meta.snapshot,
    },
  );
  if (!result.ok) {
    const outcome = await handleDivergence(result.current);
    return outcome === "remote" || outcome === "conflict";
  }
  await DrawingStore.updateMeta(meta.id, (current) =>
    current
      ? {
          ...current,
          serverVersion: result.version,
          // still dirty if it was modified while we were pushing
          dirty: current.localSavedAt !== meta.localSavedAt,
          snapshot: false,
        }
      : undefined,
  );
  return true;
};

/** folders are plain metadata: the most recent change wins */
const syncFolders = async (changed: Set<string>) => {
  const remote = await LibraryAPI.listFolders();
  const localById = new Map(
    (await DrawingStore.listFolders()).map((folder) => [folder.id, folder]),
  );
  for (const folder of remote) {
    const local = localById.get(folder.id);
    if (
      !local ||
      folder.updatedAt > local.updatedAt ||
      (!local.dirty && folder.updatedAt !== local.updatedAt)
    ) {
      await DrawingStore.putFolder({ ...folder, dirty: false });
      changed.add(folder.id);
    }
  }
  for (const folder of await DrawingStore.listFolders()) {
    if (!folder.dirty) {
      continue;
    }
    const { dirty, ...data } = folder;
    const stored = await LibraryAPI.putFolder(data, getDeviceId());
    const current = await DrawingStore.getFolder(folder.id);
    // keep changes made while pushing
    if (current && current.updatedAt === folder.updatedAt) {
      await DrawingStore.putFolder({ ...stored, dirty: false } as Folder);
      if (stored.updatedAt !== folder.updatedAt) {
        changed.add(folder.id);
      }
    }
  }
};

/** the local library was emptied because another user logged in, or got
 * back the changes put aside for this user: the page must be reloaded */
let userChanged = false;

/**
 * The local library belongs to the user who synced it. When someone else
 * logs in on this browser it is emptied before syncing, so a user's drawings
 * never end up in another user's library. Local copies made before libraries
 * were per-user are kept by the user who got that library.
 *
 * Changes the previous user hadn't synced yet (e.g. made offline) are put
 * aside first, and put back when that user logs in here again: then they
 * sync as usual (a conflict asks, as always). A copy from before libraries
 * were per-user is put aside as "" and goes back to the user who got that
 * library.
 */
const checkLibraryUser = async () => {
  const { user, legacyOwner } = await LibraryAPI.me();
  const owner = getLibraryUser();
  if (owner === user) {
    return true;
  }
  const isEmpty =
    !(await DrawingStore.listMeta()).length &&
    !(await DrawingStore.listFolders()).length;
  const keep = isEmpty || (owner === null && legacyOwner);
  if (!keep) {
    const stashed = await DrawingStore.stashPending(owner ?? "");
    if (stashed) {
      console.info(
        `drawings library: ${stashed} unsynced drawing(s) of the previous user kept aside`,
      );
    }
    await DrawingStore.clearAll();
  }
  let restored = await DrawingStore.restorePending(user);
  if (legacyOwner) {
    restored += await DrawingStore.restorePending("");
  }
  if (restored) {
    console.info(
      `drawings library: ${restored} unsynced drawing(s) of ${user} restored`,
    );
  }
  setLibraryUser(user);
  // the editor and the library page show what was there before
  return keep && !restored;
};

const runSync = async (): Promise<DrawingId[]> => {
  const changed = new Set<DrawingId>();
  if (!(await checkLibraryUser())) {
    userChanged = true;
    return [];
  }
  if (isDeviceNameDirty()) {
    await LibraryAPI.putDevice(getDeviceId(), getDeviceName());
    clearDeviceNameDirty();
  }
  await syncFolders(changed);
  const remote = await LibraryAPI.list();
  const remoteIds = new Set(remote.map((drawing) => drawing.id));
  const localById = new Map(
    (await DrawingStore.listMeta()).map((meta) => [meta.id, meta]),
  );

  // pull
  for (const remoteMeta of remote) {
    const local = localById.get(remoteMeta.id);
    const knownVersion = local?.conflict
      ? local.conflict.serverVersion
      : local?.serverVersion;
    if (
      local &&
      knownVersion !== undefined &&
      remoteMeta.version <= knownVersion
    ) {
      continue;
    }
    if (remoteMeta.purged) {
      // keep local copies with live edits: they'll be pushed back below
      if (local && !(local.dirty && !local.deleted && !local.purged)) {
        await DrawingStore.remove(remoteMeta.id);
        changed.add(remoteMeta.id);
      }
      continue;
    }
    if (local?.purged) {
      continue;
    }
    const drawing = await LibraryAPI.get(remoteMeta.id);
    if (!drawing?.scene || drawing.purged) {
      continue;
    }
    // re-read: the editor may have saved in the meantime
    const current = await DrawingStore.getMeta(remoteMeta.id);
    if (current?.dirty) {
      await handleDivergence(drawing);
    } else {
      await storeServerCopy(drawing);
    }
    changed.add(remoteMeta.id);
  }

  // push (drawings in conflict wait for the user)
  const pushPending = async () => {
    let again = false;
    for (const meta of await DrawingStore.listMeta()) {
      const lostOnServer = meta.serverVersion > 0 && !remoteIds.has(meta.id);
      if (!meta.conflict && (meta.dirty || lostOnServer)) {
        if (!(await pushDrawing(meta))) {
          again = true;
        }
        if (!meta.purged) {
          changed.add(meta.id);
        }
      }
    }
    return again;
  };
  if (await pushPending()) {
    await pushPending();
  }

  if (changed.size) {
    await DrawingStore.collectGarbageFiles();
  }
  return [...changed];
};

// -----------------------------------------------------------------------------
// login (SSO proxy in front of the app)
// -----------------------------------------------------------------------------

const LOGIN_REDIRECT_KEY = "exdl-login-redirect";
const LOGIN_REDIRECT_MIN_INTERVAL_MS = 2 * 60 * 1000;

/** login page from the last 401, if the proxy announced one */
let loginUrl: string | null = null;

/** goes to the login page, which comes back to this page afterwards (the
 * page URL is passed as `rd`, as oauth2-proxy expects); without a login page
 * reloads, which reaches the proxy unless a service worker serves the app */
const signIn = () => {
  if (!loginUrl) {
    window.location.reload();
    return;
  }
  try {
    sessionStorage.setItem(LOGIN_REDIRECT_KEY, String(Date.now()));
  } catch {}
  const url = new URL(loginUrl, window.location.href);
  url.searchParams.set("rd", window.location.href);
  window.location.assign(url.toString());
};

/** the page was opened with an expired session (typically served by the
 * service worker, so the proxy never saw the navigation): logs in right
 * away, unless that was just tried in this tab */
const autoSignIn = () => {
  let lastAttempt = 0;
  try {
    lastAttempt = Number(sessionStorage.getItem(LOGIN_REDIRECT_KEY)) || 0;
  } catch {}
  if (loginUrl && Date.now() - lastAttempt > LOGIN_REDIRECT_MIN_INTERVAL_MS) {
    signIn();
  }
};

// -----------------------------------------------------------------------------
// sync
// -----------------------------------------------------------------------------

let running: Promise<void> | null = null;
let rerunRequested = false;

const syncNow = (): Promise<void> => {
  if (running) {
    rerunRequested = true;
    return running;
  }
  running = (async () => {
    do {
      rerunRequested = false;
      if (!navigator.onLine) {
        setStatus({ state: "offline" });
        break;
      }
      setStatus({ state: "syncing", error: null });
      try {
        const changed = await withLock(runSync);
        if (userChanged) {
          // the editor and the library page show outdated data
          window.location.assign("/");
          return;
        }
        emitLibraryChange(changed, "sync");
        setStatus({ state: "idle", lastSyncAt: Date.now() });
      } catch (error: any) {
        console.warn("drawings library sync failed", error);
        const signedOut =
          error instanceof LibraryAPIError && error.status === 401;
        if (signedOut) {
          loginUrl = error.loginUrl;
        }
        setStatus({
          state: !navigator.onLine
            ? "offline"
            : signedOut
            ? "signedOut"
            : "error",
          error: error?.message ?? String(error),
        });
        // nothing synced yet since the page was opened: no editing to
        // interrupt, local changes stay in IndexedDB
        if (signedOut && status.lastSyncAt === null) {
          autoSignIn();
        }
      }
    } while (rerunRequested);
    await refreshPendingCount();
  })().finally(() => {
    running = null;
  });
  return running;
};

let scheduledSync: number | null = null;

const requestSync = (delay = SYNC_AFTER_CHANGE_DELAY_MS) => {
  if (scheduledSync !== null) {
    window.clearTimeout(scheduledSync);
  }
  scheduledSync = window.setTimeout(() => {
    scheduledSync = null;
    syncNow();
  }, delay);
};

// -----------------------------------------------------------------------------
// change notifications from the server
// -----------------------------------------------------------------------------

let events: EventSource | null = null;
let eventsConnected = false;
let eventsRetry: number | null = null;
let eventsRetryDelay = EVENTS_RETRY_MIN_MS;
/** the stream was open before: changes may have been missed meanwhile */
let eventsWereConnected = false;

const onServerChange = (event: MessageEvent) => {
  try {
    const data = JSON.parse(event.data);
    // our own writes (from this browser) are already applied locally
    if (data?.deviceId && data.deviceId === getDeviceId()) {
      return;
    }
  } catch {
    // sync anyway
  }
  if (document.hidden) {
    // the visible tab (if any) syncs and notifies the others; this one syncs
    // when it becomes visible again
    return;
  }
  requestSync(SYNC_AFTER_EVENT_DELAY_MS);
};

const closeEvents = () => {
  events?.close();
  events = null;
  eventsConnected = false;
  if (eventsRetry !== null) {
    window.clearTimeout(eventsRetry);
    eventsRetry = null;
  }
};

const openEvents = () => {
  if (events || !navigator.onLine || typeof EventSource === "undefined") {
    return;
  }
  const source = new EventSource(`${LIBRARY_API_URL}/events`);
  events = source;
  source.addEventListener("open", () => {
    eventsConnected = true;
    eventsRetryDelay = EVENTS_RETRY_MIN_MS;
    // changes made while the stream was down were not announced
    if (eventsWereConnected) {
      requestSync(SYNC_AFTER_EVENT_DELAY_MS);
    }
    eventsWereConnected = true;
  });
  source.addEventListener("changed", onServerChange);
  source.addEventListener("folders", onServerChange);
  source.addEventListener("error", () => {
    eventsConnected = false;
    if (source.readyState === EventSource.CLOSED && events === source) {
      // not retried by the browser (e.g. backend missing): try again later
      events = null;
      eventsRetry = window.setTimeout(() => {
        eventsRetry = null;
        openEvents();
      }, eventsRetryDelay);
      eventsRetryDelay = Math.min(eventsRetryDelay * 2, EVENTS_RETRY_MAX_MS);
    }
  });
};

let started = false;

export const SyncEngine = {
  getStatus: () => status,

  subscribe: (listener: (status: SyncStatus) => void) => {
    statusListeners.add(listener);
    return () => {
      statusListeners.delete(listener);
    };
  },

  syncNow,

  requestSync,

  signIn,

  /** fetches a single drawing from the server (e.g. opened from a link
   * before the library was synced) */
  fetchDrawing: async (id: DrawingId) => {
    const drawing = await LibraryAPI.get(id);
    if (!drawing?.scene || drawing.purged) {
      return false;
    }
    await storeServerCopy(drawing);
    emitLibraryChange([id], "sync");
    return true;
  },

  start: () => {
    if (started) {
      return;
    }
    started = true;
    window.addEventListener("online", () => {
      closeEvents();
      eventsRetryDelay = EVENTS_RETRY_MIN_MS;
      openEvents();
      syncNow();
    });
    window.addEventListener("offline", () => {
      closeEvents();
      setStatus({ state: "offline" });
    });
    document.addEventListener("visibilitychange", () => {
      if (!document.hidden) {
        requestSync(500);
      }
    });
    // another tab emptied the local library for a different user
    window.addEventListener("storage", (event) => {
      if (
        event.key === LIBRARY_USER_KEY &&
        event.oldValue !== null &&
        event.newValue !== event.oldValue
      ) {
        window.location.assign("/");
      }
    });
    window.setInterval(() => {
      if (
        document.hidden ||
        (eventsConnected &&
          status.lastSyncAt !== null &&
          Date.now() - status.lastSyncAt < SYNC_INTERVAL_CONNECTED_MS)
      ) {
        return;
      }
      syncNow();
    }, SYNC_INTERVAL_MS);
    onLibraryChange((event) => {
      if (event.origin !== "sync") {
        refreshPendingCount();
        requestSync();
      }
    });
    refreshPendingCount();
    openEvents();
    syncNow();
  },
};
